/**
 * C87, the shared upload control (fleet contract §16.9, Christian 2026-11-04): the queue behind it. Pure state, no
 * network, so every app lifts it verbatim and plugs in its own uploader.
 *
 * - **Many at once:** `add(files)` takes any number of files in one go (drop, browse or paste).
 * - **Errors per file, never collapsed:** a file of the wrong type or over the size limit is kept as its OWN row with its
 *   own error (`type` | `size`), and is never uploaded. An upload that fails marks only its own row (`failed`). Past the
 *   file limit, the extra files are rows with the error `limit`.
 * - **Caption per file:** `setCaption(id, text)`. It travels with the file to the uploader.
 * - **Remove before send:** `remove(id)` drops any row that is not uploading.
 * - **Progress per file:** `upload(uploader)` sends the pending rows one at a time, reporting each row's 0–100 progress.
 */
import { useCallback, useRef, useState } from 'react';

export type UploadError = 'type' | 'size' | 'limit' | 'failed';
export type UploadStatus = 'pending' | 'uploading' | 'done' | 'failed' | 'rejected';

export interface UploadItem {
  id: string;
  file: File;
  caption: string;
  status: UploadStatus;
  /** 0–100 while uploading; 100 once done. */
  progress: number;
  error?: UploadError;
}

export interface UploadRules {
  /** MIME types accepted (e.g. `image/png`), or `['*']` for any. */
  accept: readonly string[];
  maxBytes: number;
  maxFiles: number;
}

/** Sends one file with its caption, reporting progress 0–100; throws when the upload fails. */
export type Uploader = (file: File, caption: string, onProgress: (percent: number) => void) => Promise<void>;

let seq = 0;
const nextId = () => `upload-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export function acceptsType(rules: UploadRules, file: File): boolean {
  return rules.accept.includes('*') || rules.accept.includes(file.type);
}

export function useUploadQueue(rules: UploadRules) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const itemsRef = useRef<UploadItem[]>(items);
  const commit = useCallback((next: UploadItem[]) => { itemsRef.current = next; setItems(next); }, []);
  const patch = useCallback((id: string, change: Partial<UploadItem>) => {
    commit(itemsRef.current.map(i => (i.id === id ? { ...i, ...change } : i)));
  }, [commit]);

  const add = useCallback((files: File[]) => {
    const next = [...itemsRef.current];
    let accepted = next.filter(i => i.status !== 'rejected').length;
    for (const file of files) {
      let error: UploadError | undefined;
      if (!acceptsType(rules, file)) error = 'type';
      else if (file.size > rules.maxBytes) error = 'size';
      else if (accepted >= rules.maxFiles) error = 'limit';
      else accepted++;
      next.push({ id: nextId(), file, caption: '', status: error ? 'rejected' : 'pending', progress: 0, error });
    }
    commit(next);
  }, [rules, commit]);

  const remove = useCallback((id: string) => {
    commit(itemsRef.current.filter(i => i.id !== id || i.status === 'uploading'));
  }, [commit]);

  const setCaption = useCallback((id: string, caption: string) => patch(id, { caption }), [patch]);

  /** Uploads every pending (or previously failed) row, one at a time. Resolves with the counts. */
  const upload = useCallback(async (uploader: Uploader) => {
    let done = 0;
    let failed = 0;
    for (const item of itemsRef.current.filter(i => i.status === 'pending' || i.status === 'failed')) {
      patch(item.id, { status: 'uploading', progress: 0, error: undefined });
      try {
        await uploader(item.file, item.caption.trim(), p => patch(item.id, { progress: Math.max(0, Math.min(100, Math.round(p))) }));
        patch(item.id, { status: 'done', progress: 100 });
        done++;
      } catch {
        patch(item.id, { status: 'failed', error: 'failed' });
        failed++;
      }
    }
    return { done, failed };
  }, [patch]);

  const clear = useCallback(() => commit([]), [commit]);
  const sendable = items.filter(i => i.status === 'pending' || i.status === 'failed');

  return { items, add, remove, setCaption, upload, clear, sendableCount: sendable.length, rules };
}

export type UploadQueue = ReturnType<typeof useUploadQueue>;
